<template>
  <div
    ref="element"
    class="candidate-icon relative point"
    @mousedown="onMouseDown"
    @touchstart="onTouchDown"
    @touchmove="onPointerMove"
    @touchend="onPointerUp"
    @touchcancel="onPointerUp"
    @click="onClick"
    :class="{
      'candidate-draggable': candidateDraggable,
      'candidate-icon-small': small
    }"
    :style="{
      color:
        !showPosition || position > 0
          ? 'var(--ui-interaction-dark)'
          : 'var(--ui-interaction-light)'
    }"
  >
    <div
      v-if="!dragMode || cloneOnDrag"
      class="candidate-background absolute top-0 left-0 w-full h-full pointer-events-none"
      :class="{
        shadow: candidateDraggable,
        'bg-gray-100': teamPlaces > 0,
        'bg-white': (teamPlaces === 0 && candidateDraggable) || opaque
        /*'bg-gray-300': dragMode*/
      }"
    ></div>
    <div
      class="absolute top-0 left-0 w-full h-full"
      :style="{ visibility: dragMode && !cloneOnDrag ? 'hidden' : null }"
    >
      <div
        class="text-chplus-candidate-name absolute top-0"
        style="left: 0.2rem; color: var(--interaction-dark)"
        v-show="showPosition && position > 0"
      >
        {{ position }}
      </div>
      <div
        :class="{
          'text-color-regular':
            candidate.matchType === MatchType.Like ||
            candidate.matchType === MatchType.Love,
          'text-interaction-dark':
            candidate.matchType !== MatchType.Like &&
            candidate.matchType !== MatchType.Love,
          'text-chplus-candidate-match-s': !small,
          'text-chplus-s': small
        }"
        v-show="showMatchPercent"
      >
        {{ Math.round(candidate.matchPercent) | decimal("0")
        }}<span
          :class="{
            'text-chplus-s': !small,
            'text-chplus-xs': small
          }"
          >%</span
        >
      </div>
      <candidate-portrait
        :candidate="candidate"
        :position="position"
        :show-position="showPosition"
        :small="small"
        :team-places="teamPlaces"
      />
      <div
        class="candidate-name text-center leading-none max-w-full overflow-hidden"
        style="margin-top:0.15rem;"
        :class="{
          'text-color-regular':
            candidate.matchType === MatchType.Like ||
            candidate.matchType === MatchType.Love,
          'text-interaction-dark':
            candidate.matchType !== MatchType.Like &&
            candidate.matchType !== MatchType.Love,
          'text-chplus-xs': small,
          'text-chplus-s': !small,
          'break-word': !truncateName,
          'whitespace-no-wrap': truncateName,
          'overflow-hidden': truncateName,
          'small-name': small
        }"
        :style="{
          'text-overflow': 'ellipsis'
        }"
        v-show="showName"
      >
        {{ candidate.lastName }}
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-unreachable */
import { mapGetters } from "vuex";

import numeral from "numeral";

import { ViewportHelper } from "@/utils/ViewportHelper";
import CandidatePortrait from "./CandidatePortrait";
import { MatchType } from "@/utils/MatchType";

export default {
  name: "CandidateIcon",
  components: { CandidatePortrait },
  props: {
    uid: {
      type: String,
      required: true
    },
    candidateId: {
      type: Number,
      required: true
    },
    /**
     * The candidate's position in the user's ranking
     */
    position: {
      type: Number,
      default: null
    },
    showPosition: {
      type: Boolean,
      required: false,
      default: false
    },
    showMatchPercent: {
      type: Boolean,
      required: false,
      default: false
    },
    showName: {
      type: Boolean,
      required: false,
      default: false
    },
    showElected: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * @var Provides context for which list this candidate is looking at to check their seat count. Takes the dream
     * team if null.
     */
    activeList: {
      type: Object,
      default: null
    },
    candidateDraggable: {
      type: Boolean,
      required: false,
      default: false
    },
    cloneOnDrag: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      required: false,
      default: false
    },
    truncateName: {
      type: Boolean,
      default: false
    },
    /**
     * Adds a white background, used when the candidate covers other content
     */
    opaque: {
      type: Boolean,
      default: false
    }
  },
  data: () => {
    return {
      MatchType: MatchType,
      dragTimeout: 200,
      dragHoldTimeout: 500,
      minHorizontalDragDistance: 5,
      minVerticalDragDistance: 5,
      interactionId: null,
      isTouchInteraction: false,
      dragReady: false, // to prevent mixup of drag and scroll
      dragging: false, // for drag logic
      dragMode: false, // for view update
      dragStartX: -1,
      dragStartY: -1,
      pointerDeltaX: 0,
      pointerDeltaY: 0
    };
  },
  filters: {
    decimal(number, format) {
      return numeral(number).format(format);
    }
  },
  computed: {
    ...mapGetters("candidate", { getCandidate: "candidate" }),
    ...mapGetters("list", ["lists", "choice"]),
    candidate() {
      return this.getCandidate(this.candidateId);
    },
    teamPlaces() {
      let list = this.activeList ?? this.chosenList;
      return list
        ? list.candidates.filter(c => c.id === this.candidateId).length
        : 0;
    },
    chosenList() {
      const choice = this.choice;
      return choice ? this.lists.find(l => l.id === choice) : null;
    }
  },
  methods: {
    onMouseDown(event) {
      if (process.env.VUE_APP_PLATFORM === "ios") {
        // iOS fires mouse events after touchend, even after dragging which causes funky behaviour
        return;
      }
      if (!this.candidateDraggable || this.interactionId !== null) {
        return;
      }
      this.isTouchInteraction = false;
      this.onPointerDown(event);
    },
    onTouchDown(event) {
      if (!this.candidateDraggable) {
        return;
      }
      this.isTouchInteraction = true;
      document.body.style.userSelect = "pan-x pan-y";
      document.body.style.webkitUserSelect = "none";
      this.onPointerDown(event);
    },
    onPointerDown(event) {
      event.stopPropagation();

      const interactionId = this.$uuid.v4();
      const pointerPosition = ViewportHelper.getViewportPosition(event);
      const elementPosition = ViewportHelper.getElementViewportPosition(
        this.$refs["element"]
      );
      const posX = elementPosition.x;
      const posY = elementPosition.y;

      this.hookDragListeners();
      this.dragging = false;
      this.dragReady = false;
      this.interactionId = interactionId;
      this.dragStartX = pointerPosition.x;
      this.dragStartY = pointerPosition.y;
      this.pointerDeltaX = pointerPosition.x - posX;
      this.pointerDeltaY = pointerPosition.y - posY;

      let selection = window.getSelection();
      if (selection) {
        selection.empty();
      }

      this.$emit("candidatepointerdown");

      if (this.isTouchInteraction) {
        setTimeout(() => {
          if (this.interactionId !== interactionId) {
            return;
          }
          this.dragReady = true;
        }, this.dragTimeout);
      } else {
        this.dragReady = true;
      }
    },
    onPointerMove(event) {
      if (this.dragging || !this.interactionId) {
        return;
      }

      const pointerPosition = ViewportHelper.getViewportPosition(event);
      const dragDeltaX = pointerPosition.x - this.dragStartX;
      const dragDeltaY = pointerPosition.y - this.dragStartY;
      const triggeredX = Math.abs(dragDeltaX) >= this.minHorizontalDragDistance;
      const triggeredY =
        Math.abs(dragDeltaY) - Math.abs(dragDeltaX) / 2 >=
        this.minVerticalDragDistance;
      if (!triggeredX && !triggeredY) {
        return;
      }

      if (this.isTouchInteraction && !this.dragReady && triggeredY) {
        this.dragMode = false;
        this.interactionId = null;
        this.releaseDragHooks();
        return;
      }
      // alert("drag start! "+this.isTouchInteraction+","+this.dragReady+","+triggeredX+","+triggeredY);
      this.onDragStart(event);
    },
    onPointerUp() {
      this.releaseDragHooks();
      if (this.dragging) {
        this.onDragEnd();
      }
      document.body.style.userSelect = null;
      document.body.style.webkitUserSelect = null;
      setTimeout(() => {
        this.interactionId = null;
      }, 200);
    },
    onClick(event) {
      if (this.dragging) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    // eslint-disable-next-line no-unused-vars
    onDragStart(event) {
      if (this.isTouchInteraction) {
        this.$emit("touchend"); // ensure touch scroll is interrupted
      }
      this.dragging = true;
      const interactionId = this.interactionId;
      setTimeout(() => {
        if (this.interactionId !== interactionId) {
          return;
        }
        this.dragMode = true;
      }, 11);

      const elementPosition = ViewportHelper.getElementViewportPosition(
        this.$refs["element"]
      );
      const posX = elementPosition.x;
      const posY = elementPosition.y;
      // const dragDeltaX = event.pageX - this.dragStartX;
      // const dragDeltaY = event.pageY - this.dragStartY;
      this.$emit("candidatedragstart", {
        uid: this.uid,
        candidateId: this.candidateId,
        posX: posX, // x position of this candidate
        posY: posY, // y position of this candidate
        deltaX: this.pointerDeltaX, // x distance between pointer position and this candidate when interaction started
        deltaY: this.pointerDeltaY, // y distance between pointer position and this candidate when interaction started
        initiationDeltaX: 0, //dragDeltaX, // x distance the cursor moved to trigger the drag
        initiationDeltaY: 0, //dragDeltaY, // y distance the cursor moved to trigger the drag
        isTouchInteraction: this.isTouchInteraction
      });
    },
    onDragEnd() {
      this.dragging = false;
      this.dragMode = false;
    },
    hookDragListeners() {
      if (this.isTouchInteraction) {
        document.addEventListener("contextmenu", this.onContextMenu);
      } else {
        document.addEventListener("mousemove", this.onPointerMove);
        document.addEventListener("mouseup", this.onPointerUp);
      }
      document.addEventListener("selectstart", this.onSelectStart);
      document.addEventListener("selectionchange", this.onSelectStart);
      document.addEventListener("select", this.onSelect);
    },
    releaseDragHooks() {
      if (this.isTouchInteraction) {
        document.removeEventListener("contextmenu", this.onContextMenu);
      } else {
        document.removeEventListener("mousemove", this.onPointerMove);
        document.removeEventListener("mouseup", this.onPointerUp);
      }
      document.removeEventListener("selectstart", this.onSelect);
      document.removeEventListener("selectionchange", this.onSelect);
      document.removeEventListener("select", this.onSelect);
    },
    onSelect(event) {
      event.stopPropagation();
      event.preventDefault();
    },
    onContextMenu(event) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
};
</script>

<style lang="scss" scoped>
.previously-selected .candidate-icon {
  background: var(--interaction-light);
}
.previously-selected.fadeout .candidate-icon {
  transition: background 600ms ease-in-out;
  background: none;
}
.candidate-icon {
  -webkit-touch-callout: none !important;
  touch-action: pan-y;
  width: 6rem;
  /* height: 7.8125rem; */
  height: 8.6rem;
}
.candidate-icon-small {
  width: 4.2rem;
  height: 6.2rem;
}
.candidate-background,
.candidate-icon {
  border-radius: 0.5rem;
}

#app.mouse .candidate-icon:hover {
  cursor: pointer;
}
.candidate-background.shadow {
  box-shadow: var(--interaction-light) 0 0 0.3rem;
}
#app.mouse .candidate-icon:hover .candidate-background {
  box-shadow: var(--interaction-dark) 0 0.05rem 0.5rem;
  /* box-shadow: rgba(0, 10, 20, 0.2) 0 0.05em 0.2em;*/
}
.candidate-name {
  height: 3.15em;
}
.candidate-name.small-name {
  height: 2.1em;
}

@media (min-width: 768px) {
  .candidate-icon {
    width: 7.05rem;
    height: 10.1rem;
  }
  .candidate-icon-small {
    width: 4.125rem;
    height: 6.3125rem;
  }
}
@media (max-width: 380px), (max-height: 600px) {
  .candidate-name {
    max-height: 2.1em;
  }
}
</style>
